/**
 * TonightsSpendPanel.jsx — iter265.4
 * Live LLM dollar-burn tile for the admin Pipeline & Queues group.
 * Polls /api/admin/pipeline/tonights-spend every 15s; renders a progress
 * bar (green <50%, yellow 50-80%, red >80%) + today's per-job breakdown.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_CLASSES = {
  green: { bar: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  yellow: { bar: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  red: { bar: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200" },
};

function formatUSD(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

export default function TonightsSpendPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchSpend = async () => {
    try {
      const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
      const r = await axios.get(`${API}/api/admin/pipeline/tonights-spend`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    fetchSpend();
    const id = setInterval(fetchSpend, 15000);
    return () => clearInterval(id);
  }, []);

  if (error) {
    return (
      <div className="p-4 text-sm text-red-600" data-testid="tonights-spend-error">
        Failed to load spend data: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-4 text-sm text-slate-500" data-testid="tonights-spend-loading">
        Loading…
      </div>
    );
  }

  const c = COLOR_CLASSES[data.color] || COLOR_CLASSES.green;
  const pct = Math.min(100, data.pct_of_budget || 0);

  return (
    <div className="space-y-4" data-testid="tonights-spend-panel">
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-xs uppercase tracking-wide text-slate-500">Monthly LLM Spend</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${c.chip}`}
                data-testid="tonights-spend-color-chip">
            {data.color.toUpperCase()} · {pct.toFixed(1)}%
          </span>
        </div>
        <div className="text-2xl font-bold text-slate-900" data-testid="tonights-spend-monthly-total">
          {formatUSD(data.monthly_spend)} / {formatUSD(data.monthly_budget)}
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-2">
          <div
            className={`h-full ${c.bar} transition-all duration-500`}
            style={{ width: `${pct}%` }}
            data-testid="tonights-spend-progress-bar"
          />
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {data.monthly_calls.toLocaleString()} calls · {formatUSD(data.remaining)} remaining
        </div>
      </div>

      <div className="border-t pt-3">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Today (UTC)</div>
        <div className="text-lg font-semibold text-slate-800" data-testid="tonights-spend-today-total">
          {formatUSD(data.today_total_cost)} · {data.today_total_calls.toLocaleString()} calls
        </div>
        {data.today_breakdown && data.today_breakdown.length > 0 && (
          <div className="mt-2 space-y-1">
            {data.today_breakdown.map((row) => (
              <div
                key={row.job_type}
                className="flex items-center justify-between text-xs px-2 py-1 bg-slate-50 rounded"
                data-testid={`tonights-spend-row-${row.job_type}`}
              >
                <span className="font-mono text-slate-700">{row.job_type}</span>
                <span className="text-slate-500">
                  {row.calls.toLocaleString()} · {formatUSD(row.cost)}
                </span>
              </div>
            ))}
          </div>
        )}
        {(!data.today_breakdown || data.today_breakdown.length === 0) && (
          <div className="text-xs text-slate-400 mt-1">No AI calls yet today.</div>
        )}
      </div>
    </div>
  );
}
